import { collectTags, keepSelection } from "../lib/nodeTags";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, tryApiGet } from "../api/client";
import { usePolling } from "../api/usePolling";
import { useGlobalQuery } from "../api/useGlobalQuery";
import { useWebSocket, type WsConnState } from "../api/useWebSocket";
import { useAuth } from "./AuthContext";
import type {
  AuditRecomputeStatus,
  CorescorePacketPath,
  CorescorePacketsResponse,
  DisplaySettingsResponse,
  FeatureDef,
  HealthResponse,
  MeshNode,
  MeshviewPacket,
  MeshviewPacketsResponse,
  MeshviewRouteTreeResponse,
  MessageRecord,
  MessagesResponse,
  NodeDetail,
  ObserverInfo,
  Packet,
  RegionDef,
  SimulatedNode,
  SimulatedNodesResponse,
  StatsResponse,
  WsEvent,
} from "../api/types";
import {
  AnimatedPacket,
  capAnimations,
  makeAnimatedPacket,
  MESHVIEW_CAP_ADJUST_INTERVAL_MS,
  MESHVIEW_CAP_DEFAULT,
  MESHVIEW_CAP_MAX,
  MESHVIEW_CAP_MIN,
  MESHVIEW_CAP_MIN_SAMPLES,
  MESHVIEW_CAP_STEP,
  MESHVIEW_FRAME_MS_FAST_THRESHOLD,
  MESHVIEW_FRAME_MS_SLOW_THRESHOLD,
  MESHVIEW_PLAYBACK_LAG_MS,
  MESHVIEW_QUEUE_MAX,
  MESHVIEW_QUEUE_TICK_MS,
  MESHVIEW_RECEPTION_FADE_MS,
  MESHVIEW_RECEPTION_HOLD_MS,
  pruneExpired,
} from "../lib/animation";
import { CORESCORE_MAX_CONCURRENT_PATHS } from '../lib/definitions/map';
import { DEFAULT_PROVENANCE_SCOPE, type ProvenanceScope } from '../lib/definitions/trust';
import { messageStateRank } from '../lib/definitions/messaging';
import { meshtasticPortnumName, meshviewQueuePriorityForPortnum, type MeshviewQueuePriority, type Protocol } from '../lib/definitions/protocols';
import { staleDataNote } from '../lib/definitions/health';
import { availableProtocols as computeAvailableProtocols } from "../lib/transportProtocol";
import { parseTimestamp } from "../lib/format";
import { nearAnyRegionBoundary, regionsForPoint } from "../lib/regions";
import { buildRouteWalk, hasUsablePosition, meshviewPacketToPacket, pruneExpiredRouteWalks, type MeshviewRouteWalk } from "../lib/meshview";
import { buildCorescorePathWalk, pruneExpiredCorescorePaths, type CorescorePacketWalk } from "../lib/corescore";
import { SpreadScheduler } from "../lib/pathFx";

const MAX_PACKETS_KEPT = 300;
const MAX_MESSAGES_KEPT = 300;

// A "live feed" must be newest-first by timestamp, always (coordinator's
// report: a live session showed rows sorted by node id instead, unrelated
// to any of this file's own known ordering — investigated and no
// sort-by-node-id logic was found anywhere in this pipeline, and
// GET /api/packets was confirmed already returning ts-descending). Applied
// explicitly at both the points `packets` is ever set below rather than
// trusted implicitly, so the invariant holds regardless of the backend's
// own ordering or any WS/REST interleaving race — a missing/unparseable
// timestamp sorts last (never assumed newest) rather than corrupting the
// order of everything with a real one.
function byTsDesc(a: Packet, b: Packet): number {
  const ta = parseTimestamp(a.ts) ?? -Infinity;
  const tb = parseTimestamp(b.ts) ?? -Infinity;
  return tb - ta;
}

// --- Replay (timeline scrub-to-history, phase 2 §6) -----------------------
export interface ReplayState {
  active: boolean;
  atMs: number | null;
  packets: Packet[];
  loading: boolean;
  error: string | null;
}

const REPLAY_IDLE: ReplayState = { active: false, atMs: null, packets: [], loading: false, error: null };

export type Selection =
  | { kind: "node"; id: string }
  | { kind: "packet"; packet: Packet }
  | null;

export type ThemePref = "system" | "light" | "dark";

export type ProtocolFilter = "all" | Protocol;

interface AppDataValue {
  features: FeatureDef[];
  featuresError: string | null;
  featuresEverLoaded: boolean;

  health: HealthResponse | null;
  healthError: string | null;
  // Same "endpoint has actually answered, even once" honesty convention as
  // featuresEverLoaded/regionsAvailable — added for the data-layer slice
  // (2026-09-30) so NodeListPage/PacketsPage/LiveMap can tell "health
  // hasn't loaded yet" (must not show anything empty-state-shaped) apart
  // from "health loaded and says zero transports" (the real NO_CONNECTIONS
  // case) — see availableProtocols below, whose emptiness alone used to
  // conflate those two.
  healthEverLoaded: boolean;

  stats: StatsResponse | null;
  statsError: string | null;
  // Same honesty convention as healthEverLoaded above — added so
  // StatusStrip can render a neutral skeleton (never "--"/zero) on a truly
  // first-ever visit with nothing in the persisted cache yet, and never
  // afterward (the whole point of the persisted cache is that this becomes
  // true almost immediately and stays true).
  statsEverLoaded: boolean;

  // Stale-data header note (slice 2) — null means "don't show anything,"
  // per the owner's rule; a string is the exact ready-to-render copy
  // (lib/definitions.ts's staleDataNote) already formatted, e.g. "data
  // from 14:32". StatusStrip renders it verbatim when non-null.
  staleNote: string | null;

  // Protocol x provenance scoped counterpart of `stats` above (GET
  // /api/stats?protocol=...&provenance=...), always kept fresh for the
  // CURRENT protocolFilter/provenanceScope. Region is the only filter axis
  // /api/stats can't answer (region membership is computed client-side),
  // so StatusStrip uses this instead of counting its live packet buffer
  // whenever region isn't the active scope — see StatusStrip.tsx.
  scopedStats: StatsResponse | null;
  scopedStatsError: string | null;

  nodes: Map<string, MeshNode>;
  // True once GET /api/nodes has ever resolved successfully — INCLUDING a
  // resolution that came from the persisted IndexedDB cache before this
  // session's own network request landed (see lib/queryClient.ts). The
  // "loading" state (skeleton, never an empty-state message/"--"/zero) is
  // only correct while this is false.
  nodesEverLoaded: boolean;
  packets: Packet[];
  packetsEverLoaded: boolean;
  animations: AnimatedPacket[];
  overflowCount: number;

  wsState: WsConnState;
  backendReachable: boolean;

  selection: Selection;
  select: (s: Selection) => void;

  nodeDetailCache: Map<string, NodeDetail>;
  loadNodeDetail: (id: string) => Promise<NodeDetail | null>;

  themePref: ThemePref;
  setThemePref: (t: ThemePref) => void;
  effectiveTheme: "light" | "dark";

  // Which protocol(s) actually have a connected transport right now, per
  // /api/health — the protocol toggle only ever offers these: never show a
  // toggle option for a protocol with no transport.
  availableProtocols: Protocol[];
  protocolFilter: ProtocolFilter;
  setProtocolFilter: (p: ProtocolFilter) => void;

  // Tag filter shared by the Nodes tab and the map: lower-cased tag keys; a node matches when
  // it has ANY of them. Empty = no filter (lib/nodeTags.ts).
  tagFilter: string[];
  setTagFilter: (keys: string[]) => void;

  // Fourth scoping axis: a checkbox to view the full firehose or just the
  // connected radio — see lib/definitions.ts's own header
  // comment for the full reasoning (excludes, not dims; defaults to
  // connected-radio). Applied consistently by the map, feed, histogram,
  // and status strip via lib/definitions.ts's packetInProvenanceScope().
  provenanceScope: ProvenanceScope;
  setProvenanceScope: (s: ProvenanceScope) => void;

  // meshview firehose route-tree walks (live map, 2026-09-21) — held here
  // (not local to LiveMap) for the same reason `animations` is: AppData
  // owns createdAt-stamped data, LiveMap turns elapsed time into paint on
  // its own render clock. Always empty outside provenanceScope==="firehose"
  // (see the effect that populates this for the full contract) — never
  // rendered, and never even fetched, under "this radio only". Each walk is
  // a packet's inferred propagation tree (GET .../route-tree), not a flat
  // fan — see lib/meshview.ts's buildRouteWalk for why.
  meshviewRouteWalks: MeshviewRouteWalk[];
  // LiveMap.tsx calls this once per animation frame with that frame's own
  // delta (ms) so AppData's adaptive concurrency-cap controller can react
  // to how the ACTUAL device is doing, rather than a fixed benchmark-
  // derived guess — see the controller effect for the full reasoning.
  reportMeshviewFrameMs: (ms: number) => void;

  // CoreScope's own held carried-path walks (live map, 2026-09-21) — the
  // MeshCore sibling of meshviewRouteWalks above, same AppData-owns-data/
  // LiveMap-computes-frame split. Same provenance-scope contract as
  // meshview: CoreScope rows are always trust_tier "external_unauthenticated"
  // (never ownRadio), so this is only ever populated — and only ever
  // fetched at all — while provenanceScope === "firehose"; flipping back to
  // "this radio only" drops any already-held walks immediately rather than
  // letting them fade out on their own clock. See lib/corescore.ts's
  // buildCorescorePathWalk for why this is a flat carried-path walk, not a
  // route-tree (MeshCore packets carry their own real route — no inference
  // needed, unlike meshview's Meshtastic side).
  corescorePathWalks: CorescorePacketWalk[];

  // The real observer registry (GET /api/observers) — CoreScope, meshview,
  // and our own first-party transports, each with a trust tier and
  // whatever else the backend reports (kind, connection state, position).
  observers: ObserverInfo[];
  // observer_id -> trust_tier, merged from /api/observers (primary) and
  // /api/stats' observers list (fallback) — see ObserverBadge.tsx.
  observerTrust: Map<string, string>;

  // Timeline scrub-to-replay (spec §6). Entering replay does not touch the
  // live WS subscription or the map's real-time animations (see
  // TimelinePanel.tsx's header comment for why) — it swaps what the packet
  // feed displays and drives the "REPLAY" chrome.
  replay: ReplayState;
  enterReplay: (atMs: number) => void;
  exitReplay: () => void;

  // Re-runs one already-known packet's flight animation on demand (spec §5's
  // Replay action). Works in both live and replay mode since it only needs
  // the packet object itself plus whatever we currently know about the
  // endpoints' positions.
  replaySinglePacket: (packet: Packet) => void;

  // Region filter (spec §10). `regionsAvailable` is true once /api/regions
  // has responded at all (even with an empty array — real backend, zero
  // regions configured is a legitimate, honestly-shown state, not "not
  // wired up"); it's false only when the endpoint itself hasn't answered
  // (older backend, or not reachable yet).
  regions: RegionDef[];
  regionsAvailable: boolean;
  selectedRegions: Set<string>;
  setSelectedRegions: (r: Set<string>) => void;
  hideOutsideRegions: boolean;
  setHideOutsideRegions: (v: boolean) => void;
  // Every selected region a node's position falls inside, by known bbox
  // geometry only (lib/regions.ts) — empty when no region is selected, the
  // node has no position, or none of the selected regions' geometry could
  // be determined (never a wrong guess; see regions.ts).
  regionsForNode: (n: { lat: number | null; lon: number | null }) => string[];
  // Whether a node sits within a soft heuristic distance of a selected
  // region's boundary (spec §10's boundary-node marker) — false whenever no
  // region is selected. See lib/regions.ts's nearAnyRegionBoundary for the
  // (deliberately approximate) distance rule.
  nearRegionBoundary: (n: { lat: number | null; lon: number | null }) => boolean;

  // Phase 5: simulated-only send path. `messages` is fed the same way
  // `packets` is (an initial REST fetch, then live WS "message" events) but
  // upserts by id rather than always prepending, since a WS event here is
  // usually an existing message's state transition, not a brand-new
  // message. Shared here (not local to MessagesPage) purely because that's
  // the same pattern every other live list in this app already follows.
  messages: MessageRecord[];
  // Exposed so the composer can optimistically insert its own just-sent
  // message the instant POST /api/messages/send returns (same id, so the
  // real WS "message" state-transition events that follow just update this
  // same row in place rather than duplicating it) — the message list would
  // otherwise sit empty/stale until the first live event arrived.
  upsertMessage: (m: MessageRecord) => void;

  // Simulated node roster (GET /api/simulated/nodes) — shared here (not
  // local to MessagesPage) because LiveMap also renders these, distinctly
  // from real nodes, when they carry a position. `simulatedNodesAvailable`
  // follows the same "endpoint has answered at all, even with zero rows"
  // honesty convention as `regionsAvailable` above.
  simulatedNodes: SimulatedNode[];
  simulatedNodesAvailable: boolean;
  // Forces an immediate re-fetch (in addition to the background poll) —
  // called right after creating/deleting a simulated node so the UI that
  // just made the change doesn't wait out the poll interval to see it.
  refreshSimulatedNodes: () => Promise<void>;

  // GET /api/config/display (viewer-role) — map centre/zoom and unit
  // preferences every route needs, not just SettingsPage's admin-only
  // full-schema fetch. `displaySettingsAvailable` follows the same
  // "endpoint has actually answered" honesty convention as
  // regionsAvailable above; consumers (LiveMap, unit-formatting helpers)
  // must not assume defaults before this is true.
  displaySettings: DisplaySettingsResponse | null;
  displaySettingsAvailable: boolean;
}

const Ctx = createContext<AppDataValue | null>(null);

function idOf(n: { id: string | number } | null | undefined): string | null {
  if (n === null || n === undefined) return null;
  return String(n.id);
}

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  // Data layer (2026-09-30 slice 1): these are the "global data seen at
  // login" polls — nodes/packets/stats/scopedStats/health/features/
  // observers/regions — now backed by one shared, IndexedDB-persisted
  // react-query QueryClient (lib/queryClient.ts) instead of each owning a
  // bare setInterval+fetch. Everything else in AppData (simulated nodes,
  // messages, meshview/corescope firehose, display settings, radio/config)
  // is unconverted this slice — see the build report for the next-slice
  // list.
  const queryClient = useQueryClient();
  const featuresPoll = useGlobalQuery<FeatureDef[]>(["global", "features"], "/api/features", 15000);
  const healthPoll = useGlobalQuery<HealthResponse>(["global", "health"], "/api/health", 5000);
  const statsPoll = useGlobalQuery<StatsResponse>(["global", "stats"], "/api/stats", 5000);
  // Rarely changes (an admin edits it in Settings), so a slow poll — mainly
  // here to pick up an admin's change without a full reload, not for
  // real-time freshness. 2026-09-30 slice 2: joins the persisted global
  // set — LiveMap and unit-formatting helpers read this on first paint too
  // (see displaySettingsAvailable's own honesty-convention comment below).
  const displaySettingsPoll = useGlobalQuery<DisplaySettingsResponse>(["global", "displaySettings"], "/api/config/display", 60000);

  // Source of truth for the live node roster is now the react-query cache
  // itself (queryKey ["global","nodes"]) — GET /api/nodes seeds/refreshes
  // it on a 20s interval (nodesQuery below, replacing the old separate
  // initial-fetch effect + usePolling pair, which used to fetch /api/nodes
  // TWICE on mount), and live WS "node" events upsert straight into the
  // SAME cache entry via queryClient.setQueryData (upsertNode below) rather
  // than a parallel useState — so a live update is reflected in the
  // persisted IndexedDB copy too (persistQueryClient re-persists on cache
  // mutation), not just in-memory. `nodes` here is a derived Map view over
  // that array for the O(1)-lookup callers throughout this file already
  // expect.
  const nodesQuery = useQuery<MeshNode[]>({
    queryKey: ["global", "nodes"],
    queryFn: ({ signal }) => apiGet<MeshNode[]>("/api/nodes", signal),
    refetchInterval: 20000,
    meta: { persist: true },
  });
  const nodes = useMemo(() => {
    const m = new Map<string, MeshNode>();
    for (const n of nodesQuery.data ?? []) m.set(String(n.id), n);
    return m;
  }, [nodesQuery.data]);
  // Data-presence-based, not status-based — see useGlobalQuery.ts's
  // GlobalQueryState.everSucceeded header comment for why: react-query
  // flips `status` to "error" on ANY failed background refetch even when
  // `data` (live or persisted) is still sitting right there in the cache.
  const nodesEverLoaded = nodesQuery.data !== undefined;

  // Same pattern for packets: one query seeds/holds the live feed buffer
  // (GET /api/packets?limit=100, fetched once — always was WS-driven for
  // live updates, not polled on an interval), and addPacket (WS "packet"
  // events) upserts into the SAME cache entry via setQueryData so the
  // persisted copy stays current.
  const packetsQuery = useQuery<Packet[]>({
    queryKey: ["global", "packets"],
    queryFn: ({ signal }) => apiGet<Packet[]>("/api/packets?limit=100", signal),
    meta: { persist: true },
  });
  const packets = packetsQuery.data ?? [];
  const packetsEverLoaded = packetsQuery.data !== undefined;

  const [animations, setAnimations] = useState<AnimatedPacket[]>([]);
  // Moved up from further down this file (was declared just above
  // onWsEvent) so the meshview firehose effect below — which needs a live
  // snapshot of `nodes` without re-running on every node upsert — can share
  // this one ref instead of declaring a second, redundant one.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;

  // observer_id -> the node id that IS that observer, for our own
  // first-party transports only (meshtastic_vnode/meshcore_vnode today).
  // This is how a packet's live-map ray finds a real receiver: not the
  // packet's own `to_id` (meaningless for the 92% of live packets that
  // are broadcasts — `to_id: "^all"` resolves to no node, ever), but the
  // real station that heard it — a ray to the actual receiver, where we
  // know where the receiver is.
  //
  // Built from GET /api/radio/config (already fetched for Settings' Node
  // Configuration section) rather than a new endpoint: each entry's own
  // `identity` names that transport's own node (Meshtastic's `node_id`,
  // MeshCore's `public_key` — the same string /api/nodes keys MeshCore
  // entries by). A transport with no resolvable identity, or whose own
  // node has no known position yet, simply isn't in this map — looked up
  // against `nodes` below, a missing/unpositioned entry naturally yields
  // no ray, never a guessed one.
  //
  // MQTT-relayed and external-observer packets (path=mqtt, or an observer
  // like meshview/corescope) are NOT in this map and never will be from
  // this endpoint — checked directly against live traffic: the relaying
  // gateway_id these packets carry only exists on the separate per-packet
  // GET /api/packets/{hash}/coverage detail fetch, not on the live packet
  // stream (REST or WS) this map animates from. Resolving it here would
  // mean a network round-trip per packet before any ray could be drawn —
  // not attempted; these packets get the honest pulse-only treatment
  // (no ray) until/unless that data is added to the live stream itself.
  // 2026-09-30 slice 2: converted from a bespoke tryApiGet+setInterval pair
  // to the shared persisted query cache — this feeds LiveMap's ray drawing
  // on first paint (resolveReceiverNode below), so it joins the persisted
  // set like the rest of AppData's global-at-mount data. Rarely changes (a
  // transport's own identity is stable), matched to the same relaxed
  // cadence Node Configuration's own lazy fetch implies rather than
  // live-traffic polling frequency.
  const radioConfigPoll = useGlobalQuery<{ transport_id: string; identity?: Record<string, unknown> }[]>(
    ["global", "radioConfig"],
    "/api/radio/config",
    60000,
  );
  const ownStationNodeIdByObserver = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of radioConfigPoll.data ?? []) {
      const nodeId = e.identity?.node_id ?? e.identity?.public_key;
      if (typeof nodeId === "string" && nodeId) m.set(e.transport_id, nodeId);
    }
    return m;
  }, [radioConfigPoll.data]);

  const ownStationNodeIdByObserverRef = useRef(ownStationNodeIdByObserver);
  ownStationNodeIdByObserverRef.current = ownStationNodeIdByObserver;

  function resolveReceiverNode(packet: Packet, nodesSnapshot: Map<string, MeshNode>): MeshNode | null {
    const ownNodeId = packet.observer_id ? ownStationNodeIdByObserverRef.current.get(packet.observer_id) : undefined;
    return ownNodeId ? nodesSnapshot.get(ownNodeId) ?? null : null;
  }
  const [overflowCount, setOverflowCount] = useState(0);
  const [selection, setSelection] = useState<Selection>(null);
  const nodeDetailCache = useRef<Map<string, NodeDetail>>(new Map());
  const [, forceDetailBump] = useState(0);
  const [replay, setReplay] = useState<ReplayState>(REPLAY_IDLE);
  const { authVersion, handlePossibleAuthFailure } = useAuth();

  // Converted to the shared persisted query cache (2026-09-30 slice 1) —
  // was a bespoke tryApiGet+setInterval pair. `regionsAvailable` keeps the
  // exact same honesty convention (true once the endpoint has answered at
  // all, even with zero rows; false only while genuinely never-answered —
  // an older backend without this route, or not reachable yet).
  const regionsPoll = useGlobalQuery<RegionDef[]>(["global", "regions"], "/api/regions", 30000);
  const regions = regionsPoll.data ?? [];
  const regionsAvailable = regionsPoll.everSucceeded;
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(new Set());
  const [hideOutsideRegions, setHideOutsideRegions] = useState(false);

  // Box membership is independent per box (unlike the anchor model's
  // "nearest of every real candidate" comparison), so this can classify
  // directly against just the currently-selected regions — no need to
  // widen to the full configured set first and filter after; a point
  // either is or isn't inside a given selected box regardless of what
  // other boxes exist.
  const regionsForNode = useCallback(
    (n: { lat: number | null; lon: number | null }) => {
      if (selectedRegions.size === 0) return [];
      const selected = regions.filter((r) => r.name && selectedRegions.has(r.name));
      return regionsForPoint(n.lat, n.lon, selected);
    },
    [regions, selectedRegions],
  );

  // Boundary-node treatment (spec §10) — only meaningful against the
  // regions currently selected, same "region view" gating as regionsForNode
  // above. See lib/regions.ts's nearAnyRegionBoundary for the full
  // reasoning.
  const nearRegionBoundary = useCallback(
    (n: { lat: number | null; lon: number | null }) => {
      if (selectedRegions.size === 0) return false;
      const selected = regions.filter((r) => r.name && selectedRegions.has(r.name));
      return nearAnyRegionBoundary(n.lat, n.lon, selected);
    },
    [regions, selectedRegions],
  );

  // --- Phase 5: simulated nodes (shared with LiveMap) ---------------------
  // 2026-09-30 slice 2: converted to the shared persisted query cache — the
  // old version's own "sequence guard" existed purely to stop an
  // older-issued-but-later-resolving GET from clobbering a fresher one
  // (the poll and refreshSimulatedNodes()'s on-demand call, both in
  // flight at once, right after a create/delete in MessagesPage);
  // react-query's own refetch() already guarantees this for a single query
  // key (an in-flight fetch is deduped/cancelled rather than raced), so
  // the manual seq ref is gone, not reimplemented.
  const simulatedNodesPoll = useQuery<SimulatedNodesResponse>({
    queryKey: ["global", "simulatedNodes"],
    queryFn: ({ signal }) => apiGet<SimulatedNodesResponse>("/api/simulated/nodes", signal),
    refetchInterval: 15000,
    meta: { persist: true },
  });
  const simulatedNodes = simulatedNodesPoll.data?.nodes ?? [];
  const simulatedNodesAvailable = simulatedNodesPoll.data !== undefined;
  // Forces an immediate re-fetch — called right after creating/deleting a
  // simulated node (MessagesPage) so the UI that just made the change
  // doesn't wait out the 15s poll interval to see it.
  const refreshSimulatedNodes = useCallback(async () => {
    await simulatedNodesPoll.refetch();
  }, [simulatedNodesPoll.refetch]);

  // --- Phase 5: messages (live, like packets — initial fetch + WS upserts) -
  const [messages, setMessages] = useState<MessageRecord[]>([]);

  // Order-aware upsert — a later state must never be clobbered by an
  // earlier one (diagnosed exactly: the WS during a send emitted queued ->
  // fragmenting -> sent_to_transport -> acked correctly; the bug was
  // purely on this side). The simulated
  // transport can run that entire state machine in ~3ms — faster than the
  // POST /api/messages/send promise that kicked it off actually resolves.
  // MessagesPage's own optimistic insert fires once that promise resolves,
  // built from the POST response, which necessarily still says "queued"
  // (that was true when the request was issued) — arriving AFTER the WS
  // had already advanced this same row to "acked" moments earlier. Without
  // this guard the optimistic update wins by arriving last and the row
  // sits at "Queued" forever.
  //
  // This will never reproduce against a real radio, where an ack takes
  // seconds, not milliseconds — the instant simulated path is the one that
  // exposes the defect, not the unrealistic one; don't "fix" this by
  // slowing the simulated transport down instead.
  //
  // state_history length is the primary signal (it counts real
  // transitions, so it can't be fooled by a state string alone) with
  // messageStateRank as a tiebreak only when two updates report an
  // identical history length. Comparing history length also protects
  // against WS events themselves arriving out of order on a slower path,
  // not just this specific optimistic-update race.
  const upsertMessage = useCallback((m: MessageRecord) => {
    setMessages((prev) => {
      const idx = prev.findIndex((x) => x.id === m.id);
      if (idx < 0) return [m, ...prev].slice(0, MAX_MESSAGES_KEPT);
      const existing = prev[idx];
      const existingLen = existing.state_history?.length ?? 0;
      const incomingLen = m.state_history?.length ?? 0;
      const incomingIsBehind =
        incomingLen < existingLen || (incomingLen === existingLen && messageStateRank(m.state) < messageStateRank(existing.state));
      if (incomingIsBehind) return prev;
      const next = prev.slice();
      next[idx] = m;
      return next;
    });
  }, []);

  // 2026-09-30 slice 2: converted to the shared persisted query cache — a
  // real GET (through the same react-query machinery as the rest of
  // AppData's global data) rather than a bespoke try/catch effect. Still
  // only ever seeds `messages` ONCE per successful resolution; live
  // updates keep coming from WS "message" events via upsertMessage above,
  // same split as before (this is an initial-fetch, not a poll — the WS is
  // what keeps it current afterward).
  const messagesInitialQuery = useQuery<MessagesResponse>({
    queryKey: ["global", "messages"],
    queryFn: ({ signal }) => apiGet<MessagesResponse>("/api/messages?limit=100", signal),
    meta: { persist: true },
  });
  // Seeds `messages` from the query's data exactly ONCE per resolution the
  // ref hasn't already applied — NOT on every refetch (window focus, the
  // interval-free query's own staleTime:0 default remount refetch, etc.).
  // A repeat seed would blindly overwrite live WS-advanced message state
  // (e.g. a message already progressed to "acked" via a WS event) with
  // whatever the periodic REST snapshot says, undoing upsertMessage's own
  // order-aware guard above. First-ever seed (including straight from the
  // persisted cache, before this session's own network request lands) is
  // exactly what's wanted; every later resolution is just this query
  // staying warm for a future reload, not a live resync source.
  const messagesSeededRef = useRef(false);
  useEffect(() => {
    if (messagesInitialQuery.data && !messagesSeededRef.current) {
      messagesSeededRef.current = true;
      setMessages(messagesInitialQuery.data.messages);
    }
  }, [messagesInitialQuery.data]);

  const [protocolFilterState, setProtocolFilterState] = useState<ProtocolFilter>(() => {
    try {
      const v = localStorage.getItem("amalgamesh:protocolFilter");
      if (v === "all" || v === "meshtastic" || v === "meshcore") return v;
    } catch {
      /* private-mode / blocked storage: fine, just don't persist */
    }
    return "all";
  });
  const setProtocolFilter = useCallback((p: ProtocolFilter) => {
    setProtocolFilterState(p);
    try {
      localStorage.setItem("amalgamesh:protocolFilter", p);
    } catch {
      /* same as above */
    }
  }, []);

  // Tag filter (not persisted). A selected tag that no node carries any more drops out.
  const [tagFilterState, setTagFilter] = useState<string[]>([]);
  const tagFilter = useMemo(
    () => (tagFilterState.length === 0 ? tagFilterState : keepSelection(tagFilterState, collectTags(nodes.values()))),
    [tagFilterState, nodes],
  );

  const [provenanceScope, setProvenanceScopeState] = useState<ProvenanceScope>(() => {
    try {
      const v = localStorage.getItem("amalgamesh:provenanceScope");
      if (v === "connected_radio" || v === "firehose") return v;
    } catch {
      /* private-mode / blocked storage: fine, just don't persist */
    }
    return DEFAULT_PROVENANCE_SCOPE;
  });
  const setProvenanceScope = useCallback((s: ProvenanceScope) => {
    setProvenanceScopeState(s);
    try {
      localStorage.setItem("amalgamesh:provenanceScope", s);
    } catch {
      /* same as above */
    }
  }, []);

  const availableProtocolsList = useMemo(
    () => computeAvailableProtocols(healthPoll.data?.transports),
    [healthPoll.data],
  );

  // Defensive fallback: a persisted filter for a protocol whose transport
  // has since disappeared (or never existed on this backend) reverts to
  // "all" rather than silently showing an empty view forever. Computed
  // here (rather than down where it's returned) so the scoped-stats poll
  // below can key off the SAME clamped value everything else in the app
  // uses — fetching a raw, unclamped protocolFilterState would ask the
  // backend for a scope (e.g. "meshtastic") the rest of the UI has
  // already fallen back away from, and StatusStrip's own mismatch guard
  // would then correctly refuse to use it, silently degrading to the
  // session-buffer fallback for no visible reason.
  const protocolFilter: ProtocolFilter =
    protocolFilterState !== "all" && !availableProtocolsList.includes(protocolFilterState)
      ? "all"
      : protocolFilterState;

  // Real, backend-computed totals for the active protocol x provenance
  // scope (task 2026-09-21, two rounds: "packets total" must never fall
  // back to counting the ~100-entry live buffer when a real scoped total
  // is available, and that total must itself aggregate CoreScope's/
  // meshview's firehose tables under "firehose" scope — see
  // api/routes/stats.py's own docstring for the full contract). Always
  // enabled (region is the only scoping axis the backend can't answer —
  // see StatusStrip.tsx); the path changes with both protocolFilter
  // and provenanceScope so usePolling's own effect re-fetches on every
  // switch rather than serving a stale scope's numbers.
  const scopedStatsPoll = useGlobalQuery<StatsResponse>(
    ["global", "scopedStats", provenanceScope, protocolFilter],
    `/api/stats?provenance=${provenanceScope}${protocolFilter !== "all" ? `&protocol=${protocolFilter}` : ""}`,
    5000,
  );

  // --- meshview firehose (live map, 2026-09-21) ---------------------------
  // Every single packet hits many feeders, and it should be VERY busy on
  // the map; a decrypted packet gets its lines drawn. GET /api/meshview/packets
  // and its per-packet .../route-tree are a completely separate read path
  // from /api/packets/`/ws/live` (see api/types.ts's MeshviewPacket comment
  // for why) — nothing about the existing first-party packet pipeline above
  // changes; this is purely additive.
  //
  // Gated on provenanceScope === "firehose" at BOTH the poll (via
  // usePolling's own `enabled` arg) and the effect below, per the existing
  // provenance toggle's own contract (lib/definitions.ts: excludes, not
  // dims — meshview rows are always trust_tier "external_unauthenticated",
  // never ownRadio, so "this radio only" must show zero of them). Switching
  // back to "this radio only" stops fetching entirely, not just stops
  // rendering — and any already-held walks are dropped immediately below
  // rather than left to fade out on their own clock, so "they must
  // disappear entirely" is true the instant the toggle flips, not several
  // seconds later.
  const [meshviewRouteWalks, setMeshviewRouteWalks] = useState<MeshviewRouteWalk[]>([]);
  // meshview_packet_id is an opaque string (see api/types.ts) — never a
  // numeric key.
  const meshviewSeenRef = useRef<Set<string>>(new Set());
  const meshviewPoll = usePolling<MeshviewPacketsResponse>("/api/meshview/packets?limit=25", 2000, provenanceScope === "firehose");

  // Playback queue for the timestamp-replay fix (see lib/animation.ts's own
  // header comment on MESHVIEW_PLAYBACK_LAG_MS for the full
  // diagnosis/design history). Each entry carries the WALL-CLOCK time
  // (Date.now()-comparable) it's scheduled to release at — its own
  // imported_at plus the fixed lag — and the scheduler below releases
  // whatever is due, in the queue's own chronological order. Ordering here
  // is honest replay order, not a simulated choice; `priority` is carried
  // only so overflow eviction can protect traceroute/message packets from
  // being the one dropped when the buffer is over MESHVIEW_QUEUE_MAX (see
  // evictToCap below) — it no longer reorders WHEN anything plays.
  const meshviewQueueRef = useRef<{ packet: MeshviewPacket; releaseAtMs: number; priority: MeshviewQueuePriority }[]>([]);

  // Adaptive concurrency cap (live map): there can be a lot of packets,
  // especially when they're really moving, and it needs to look good that
  // way. A fixed cap derived from a software-rendered benchmark
  // systematically understates what a real GPU can do; this instead starts
  // HIGH and only backs off if THIS device's own measured frame time says
  // it's struggling (see the adjustment effect below), recovering when it
  // isn't. LiveMap.tsx reports each frame's own delta via
  // reportMeshviewFrameMs; kept as a ref (not state) since it's read at
  // most once per released packet, not something that should itself
  // trigger a re-render.
  const meshviewCapRef = useRef<number>(MESHVIEW_CAP_DEFAULT);
  const meshviewFrameSamplesRef = useRef<number[]>([]);

  const reportMeshviewFrameMs = useCallback((ms: number) => {
    const arr = meshviewFrameSamplesRef.current;
    arr.push(ms);
    if (arr.length > 180) arr.shift(); // a few seconds of samples at 60fps — enough to smooth a blip, short enough to react
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => {
      const arr = meshviewFrameSamplesRef.current;
      if (arr.length < MESHVIEW_CAP_MIN_SAMPLES) return;
      const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
      if (avg > MESHVIEW_FRAME_MS_SLOW_THRESHOLD) {
        meshviewCapRef.current = Math.max(MESHVIEW_CAP_MIN, meshviewCapRef.current - MESHVIEW_CAP_STEP);
      } else if (avg < MESHVIEW_FRAME_MS_FAST_THRESHOLD) {
        meshviewCapRef.current = Math.min(MESHVIEW_CAP_MAX, meshviewCapRef.current + MESHVIEW_CAP_STEP);
      }
      arr.length = 0; // fresh window for the next adjustment interval
    }, MESHVIEW_CAP_ADJUST_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Evicts down to `cap`, protecting `high`-priority entries first — a
  // prioritised packet jumps the queue and must never be the one dropped
  // when the cap bites; a position beacon can be — drops the OLDEST
  // normal-priority entries first, and only reaches into high-priority ones
  // if normal-priority alone can't make room (kept simple as a fallback;
  // measured live traffic makes this branch essentially unreachable, since
  // high-priority packets are ~5% of the mix).
  const evictToCap = useCallback(<T extends { priority: MeshviewQueuePriority }>(list: T[], cap: number): { kept: T[]; dropped: number } => {
    if (list.length <= cap) return { kept: list, dropped: 0 };
    const kept = [...list];
    let excess = kept.length - cap;
    let i = 0;
    while (excess > 0 && i < kept.length) {
      if (kept[i].priority === "normal") {
        kept.splice(i, 1);
        excess--;
      } else {
        i++;
      }
    }
    while (excess > 0) {
      kept.shift();
      excess--;
    }
    return { kept, dropped: list.length - kept.length };
  }, []);

  useEffect(() => {
    if (provenanceScope !== "firehose") {
      setMeshviewRouteWalks([]);
      meshviewQueueRef.current = [];
      return;
    }
  }, [provenanceScope]);

  // Releases exactly one newly-seen meshview packet: starts its origin
  // pulse and kicks off its route-tree fetch. This is verbatim what the old
  // effect below used to do for EVERY newly-seen packet in a poll response,
  // synchronously, in one tick — now called at a paced rate by the drain
  // scheduler further down so a burst of newly-seen rows streams in instead
  // of dumping.
  const releaseMeshviewPacket = useCallback((mp: MeshviewPacket) => {
    // The transmitting node's position is looked up from our own real node
    // roster (nodesRef — the same live snapshot addPacket/onWsEvent use
    // elsewhere), never invented: meshview reports from_node_id but no
    // position of its own for the transmitter, only for each node in its
    // route tree (see the fetch below).
    const fromNode = mp.from_node_id ? nodesRef.current.get(mp.from_node_id) ?? null : null;

    // Absolute rule: if there's no origin to put it at, it gets dropped —
    // no animation. Not a degraded fallback, not a guessed position,
    // nothing. Checked FIRST, before any animation state exists, so this
    // packet never gets a pulse started only to discover there's nowhere
    // to put it — no pulse, no route-tree fetch, no paths, no flashes.
    // Measured live (100 packets via the route-tree API, the same
    // authoritative source lib/meshview.ts's buildRouteWalk checks its own
    // root against): ~90% have a usable origin, ~10% don't — that ~10%
    // still counts in totals/feed/DB via the other, unrelated paths that
    // populate those (this function only ever touches `animations` and
    // `meshviewRouteWalks`); it simply never animates on the map. Uses the
    // exact same hasUsablePosition rule as buildRouteWalk's own root check
    // (lib/meshview.ts) — one definition of "usable," not two, since a bare
    // null check alone lets the (0,0) sentinel through.
    if (!fromNode || !hasUsablePosition(fromNode)) return;

    // The packet's own flash — reuses the existing pulse/ray animation
    // pipeline verbatim via the Packet adapter (lib/meshview.ts). `to` is
    // always null here: meshview doesn't report whether one of OUR OWN
    // stations heard this packet, so no ray is drawn for it, only the
    // origin pulse — the real "who heard it, and how it propagated" answer
    // is the route-tree walk below, drawn a different, per-path-animated
    // way (see lib/animation.ts's routeWalkPathState).
    const anim = makeAnimatedPacket(meshviewPacketToPacket(mp), fromNode, null);
    setAnimations((prev) => {
      const { next, overflowed } = capAnimations(pruneExpired(prev, performance.now()), anim);
      if (overflowed) setOverflowCount((c) => c + 1);
      return next;
    });

    if (mp.reception_summary?.observer_count) {
      const priority = meshviewQueuePriorityForPortnum(mp.portnum);
      tryApiGet<MeshviewRouteTreeResponse>(`/api/meshview/packets/${mp.meshview_packet_id}/route-tree?limit=100`)
        .then((res) => {
          if (!res) return;
          const walk = buildRouteWalk(res, performance.now(), priority);
          if (!walk) return;
          walk.category = { protocol: mp.protocol, type: mp.protocol === "meshtastic" ? meshtasticPortnumName(mp.portnum) : null };
          setMeshviewRouteWalks((prev) => {
            // Adaptive, device-measured cap (see meshviewCapRef above) —
            // priority-aware eviction, not simply oldest-first, so a
            // traceroute/message packet is never the one dropped.
            const { kept, dropped } = evictToCap([...prev, walk], meshviewCapRef.current);
            if (dropped) setOverflowCount((c) => c + dropped);
            return kept;
          });
        })
        .catch(() => {
          /* best-effort: one missed tree fetch just means this one packet's
             walk never appears, not an error state worth surfacing */
        });
    }
  }, [evictToCap]);

  // Enqueue newly-seen packets, scheduled to release at their own
  // imported_at plus the fixed replay lag (see lib/animation.ts's
  // MESHVIEW_PLAYBACK_LAG_MS header comment) — never dumped straight into
  // animation state here (see releaseMeshviewPacket/the drain scheduler
  // below).
  useEffect(() => {
    if (provenanceScope !== "firehose" || !meshviewPoll.data) return;
    const nowMs = Date.now();
    const fresh: { packet: MeshviewPacket; releaseAtMs: number; priority: MeshviewQueuePriority }[] = [];
    let droppedStale = 0;
    for (const mp of meshviewPoll.data.packets) {
      if (meshviewSeenRef.current.has(mp.meshview_packet_id)) continue;
      meshviewSeenRef.current.add(mp.meshview_packet_id);

      // Same rule: if there's no origin to put it at, it gets dropped —
      // checked HERE, before this packet ever occupies a queue slot, not
      // merely at release time (queueing an
      // animation-that-will-never-play and then silently skipping it at
      // release inflates the overflow counter with drops that were never
      // really about capacity — see releaseMeshviewPacket's own matching
      // guard, kept as defense-in-depth for the rare case a position is
      // learned/lost during the ~90s+ this packet would otherwise sit
      // queued). Not counted as overflow: this is a deliberate exclusion by
      // policy, not a capacity drop, and the packet is untouched everywhere
      // else (stats/feed/DB) — only its OWN map animation never queues.
      const originNode = mp.from_node_id ? nodesRef.current.get(mp.from_node_id) ?? null : null;
      if (!originNode || !hasUsablePosition(originNode)) continue;

      // Defensive against clock oddities: an unparseable timestamp (bad
      // payload) or one wildly in the future (clock skew) is treated as
      // "now" rather than left to corrupt the queue's chronological order
      // or stall the scheduler on a due time that never honestly arrives.
      let eventMs = Date.parse(mp.imported_at ?? "");
      if (!Number.isFinite(eventMs) || eventMs > nowMs + MESHVIEW_PLAYBACK_LAG_MS) {
        eventMs = nowMs;
      }
      const releaseAtMs = eventMs + MESHVIEW_PLAYBACK_LAG_MS;

      // Backend-hiccup recovery: a packet already more than one full lag
      // window overdue by the time we see it did not arrive late because
      // the mesh was quiet — OUR OWN fetch pipeline stalled (poll failure,
      // backend hiccup). Forcing it out now would relocate the exact
      // batch-dump bug this fix removes from the backend's 60s ingest
      // cycle to our own recovery moment. Drop it silently (counted as
      // overflow, same "count it, don't silently lose it" convention) and
      // let the playback clock keep running off real time — recovery is
      // re-syncing to now, never dumping the backlog.
      if (releaseAtMs < nowMs - MESHVIEW_PLAYBACK_LAG_MS) {
        droppedStale++;
        continue;
      }
      fresh.push({ packet: mp, releaseAtMs, priority: meshviewQueuePriorityForPortnum(mp.portnum) });
    }
    if (fresh.length > 0 || droppedStale > 0) {
      // Sort by scheduled release time, not arrival/poll order — packets
      // can arrive out of order (a later poll's response containing an
      // earlier imported_at than one already queued) and this keeps
      // release honestly chronological regardless.
      const combined = [...meshviewQueueRef.current, ...fresh].sort((a, b) => a.releaseAtMs - b.releaseAtMs);
      // Never grow an unbounded backlog — capped, dropping the oldest
      // NORMAL-priority entries first (see evictToCap) and counting it via
      // the existing overflow counter.
      const { kept, dropped } = evictToCap(combined, MESHVIEW_QUEUE_MAX);
      meshviewQueueRef.current = kept;
      if (dropped > 0 || droppedStale > 0) setOverflowCount((c) => c + dropped + droppedStale);
    }
    // Bound the seen-id set so a long-running session doesn't grow it
    // forever — trimmed well below where it'd ever matter for de-dup
    // correctness against a 25-per-poll, 2s-interval feed.
    if (meshviewSeenRef.current.size > 5000) {
      const arr = Array.from(meshviewSeenRef.current);
      meshviewSeenRef.current = new Set(arr.slice(arr.length - 2000));
    }
  }, [meshviewPoll.data, provenanceScope, evictToCap]);

  // The playback scheduler itself: on each tick, releases every queued
  // packet whose own scheduled release time (imported_at + the fixed
  // MESHVIEW_PLAYBACK_LAG_MS lag) has arrived, in chronological order —
  // preserving whatever the real gap between them was, exactly, never
  // clamped and never spread. A tight real cluster (sub-second gaps) can
  // release several packets in one tick; a real quiet stretch releases
  // nothing until it's genuinely due, and the map goes quiet — that is the
  // point (see lib/animation.ts's own header comment for the full design
  // history: this replaced an adaptive queue-depth pacer that was evenly
  // spreading playback and flattening real variation in both directions).
  useEffect(() => {
    const id = window.setInterval(() => {
      if (provenanceScope !== "firehose") return;
      const queue = meshviewQueueRef.current;
      if (queue.length === 0) return;
      const nowMs = Date.now();
      while (queue.length > 0 && queue[0].releaseAtMs <= nowMs) {
        const entry = queue.shift();
        if (!entry) break;
        releaseMeshviewPacket(entry.packet);
      }
    }, MESHVIEW_QUEUE_TICK_MS);
    return () => window.clearInterval(id);
  }, [provenanceScope, releaseMeshviewPacket]);

  // Coarse bound on route-walk memory independent of the fetch effect above
  // (belt-and-suspenders if a poll cycle stalls); the actual visible
  // fade math is LiveMap's own per-render-tick responsibility, same split
  // already used for `animations`/pruneExpired above.
  useEffect(() => {
    const id = window.setInterval(() => {
      setMeshviewRouteWalks((prev) => {
        const next = pruneExpiredRouteWalks(prev, performance.now());
        return next.length === prev.length ? prev : next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  // --- CoreScope MeshCore carried-path firehose (live map, 2026-09-21) ----
  // The MeshCore sibling of the meshview firehose block above. Genuinely
  // simpler: CoreScope's `has_path` filter (query param, backend-side) means
  // the poll only ever returns packets worth a path fetch at all — no
  // reception_summary.observer_count gate like meshview's own
  // releaseCorescorePacket needs, and no timestamp-replay playback queue —
  // CoreScope's own poll is not known to share meshview's specific 60s
  // upstream-batch artifact (that fix was scoped to a diagnosed meshview.py
  // behaviour, not re-applied speculatively here). Same provenance gating
  // as meshview: fetched ONLY while provenanceScope === "firehose" (CoreScope
  // rows are always trust_tier "external_unauthenticated", never ownRadio),
  // at both the poll (usePolling's own `enabled`) and the effects below.
  const [corescorePathWalks, setCorescorePathWalks] = useState<CorescorePacketWalk[]>([]);
  const corescoreSeenRef = useRef<Set<string>>(new Set());
  const corescorePoll = usePolling<CorescorePacketsResponse>(
    "/api/corescope/packets?limit=25&has_path=true",
    2000,
    provenanceScope === "firehose",
  );

  useEffect(() => {
    if (provenanceScope !== "firehose") setCorescorePathWalks([]);
  }, [provenanceScope]);

  // A poll can bring a whole batch of packets at once. Their animations are spread across the batch window (about
  // 60 s, scaled to the batch size and to how often batches actually arrive; lib/pathFx.ts's SpreadScheduler) instead
  // of all starting together. Only CoreScope packets are spread: our own radio's packets animate immediately.
  const corescoreSpreadRef = useRef(new SpreadScheduler());

  useEffect(() => {
    if (provenanceScope !== "firehose" || !corescorePoll.data) return;
    const fresh = corescorePoll.data.packets.filter((p) => {
      if (corescoreSeenRef.current.has(p.packet_hash)) return false;
      corescoreSeenRef.current.add(p.packet_hash);
      return true;
    });
    const batchAt = performance.now();
    const delays = corescoreSpreadRef.current.schedule(batchAt, fresh.length);
    fresh.forEach((p, i) => {
      // has_path=true was already the poll's own query filter, so every row
      // here is worth a path fetch — no separate observer-count gate like
      // meshview's own release function needs.
      tryApiGet<CorescorePacketPath>(`/api/corescope/packets/${encodeURIComponent(p.packet_hash)}/path`)
        .then((res) => {
          if (!res) return;
          // The reporting station ends the chain when it is on our roster with a position.
          const stationId = p.reporting_observer?.id ?? null;
          const station = stationId ? nodesRef.current.get(stationId) ?? nodesRef.current.get(stationId.toLowerCase()) ?? null : null;
          const observer = station && station.lat != null && station.lon != null ? { lat: station.lat, lon: station.lon } : null;
          const startAt = Math.max(performance.now(), batchAt + delays[i]);
          const walk = buildCorescorePathWalk(res, startAt, { payloadType: p.payload_type, observer });
          if (!walk) return;
          setCorescorePathWalks((prev) => {
            // Plain oldest-first FIFO cap (lib/definitions.ts's CORESCORE_MAX_CONCURRENT_PATHS), counting walks still
            // waiting for their start.
            const next = [...prev, walk];
            if (next.length <= CORESCORE_MAX_CONCURRENT_PATHS) return next;
            return next.slice(next.length - CORESCORE_MAX_CONCURRENT_PATHS);
          });
        })
        .catch(() => {
          /* best-effort: one missed path fetch just means this one packet's
             walk never appears, not an error state worth surfacing */
        });
    });
    // Bound the seen-hash set the same way meshviewSeenRef is bounded above
    // — never grows forever across a long-running session.
    if (corescoreSeenRef.current.size > 5000) {
      const arr = Array.from(corescoreSeenRef.current);
      corescoreSeenRef.current = new Set(arr.slice(arr.length - 2000));
    }
  }, [corescorePoll.data, provenanceScope]);

  // Coarse bound on walk memory, same split as meshview's own interval
  // above — the actual visible fade math is LiveMap's own per-render-tick
  // responsibility. Reuses the exact same MESHVIEW_RECEPTION_HOLD_MS/FADE_MS
  // timing meshview's own walks use (lib/animation.ts) — "the same timing
  // constants," not a second tuning.
  useEffect(() => {
    const id = window.setInterval(() => {
      setCorescorePathWalks((prev) => {
        const next = pruneExpiredCorescorePaths(prev, performance.now());
        return next.length === prev.length ? prev : next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const [themePref, setThemePrefState] = useState<ThemePref>(() => {
    try {
      return (localStorage.getItem("amalgamesh:theme") as ThemePref) || "system";
    } catch {
      return "system";
    }
  });
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false,
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const setThemePref = useCallback((t: ThemePref) => {
    setThemePrefState(t);
    try {
      localStorage.setItem("amalgamesh:theme", t);
    } catch {
      /* private-mode / blocked storage: fine, just don't persist */
    }
  }, []);

  const effectiveTheme: "light" | "dark" =
    themePref === "system" ? (systemDark ? "dark" : "light") : themePref;

  // nodesQuery (declared above) is the ONE fetch of /api/nodes — both the
  // initial load and the 20s periodic refresh (catches nodes that never
  // happen to emit a live WS event during our session, e.g. long-silent
  // ones whose "last seen" we still want reflected). This replaces what
  // used to be a duplicate pair: a one-shot effect fetching /api/nodes on
  // mount, PLUS a separate usePolling("/api/nodes", 20000) merged in on top
  // of it a few lines below — both hit the same ~1.3MB endpoint
  // independently. Live WS "node" events upsert directly into the query
  // cache (not a parallel setState) so the persisted IndexedDB copy always
  // reflects the latest live state, not just whatever the last REST poll
  // saw.
  const upsertNode = useCallback(
    (n: MeshNode) => {
      queryClient.setQueryData<MeshNode[]>(["global", "nodes"], (prev) => {
        const list = prev ? prev.slice() : [];
        const idx = list.findIndex((x) => String(x.id) === String(n.id));
        // A live node event carries no tags; keep the ones already known.
        if (idx >= 0) list[idx] = n.tags === undefined && list[idx].tags !== undefined ? { ...n, tags: list[idx].tags } : n;
        else list.push(n);
        return list;
      });
    },
    [queryClient],
  );

  const addPacket = useCallback(
    (p: Packet, nodesSnapshot: Map<string, MeshNode>) => {
      queryClient.setQueryData<Packet[]>(["global", "packets"], (prev) => {
        const list = prev ?? [];
        const key = p.hash || p.id;
        if (list.some((x) => (x.hash || x.id) === key)) return list;
        // Insert in place rather than assuming this new packet is newest
        // (see byTsDesc's own header comment above) — cheap at this array's
        // 300-entry cap and the one point-fix that makes "newest first"
        // actually true regardless of arrival order.
        return [...list, p].sort(byTsDesc).slice(0, MAX_PACKETS_KEPT);
      });

      const fromId = idOf(p.from_id ? { id: p.from_id } : null);
      const from = fromId ? nodesSnapshot.get(fromId) ?? null : null;
      // The ray's destination is the RECEIVING station, not `p.to_id` —
      // see resolveReceiverNode's own comment above (to_id is "^all" for
      // 92% of live traffic and was never a resolvable position anyway).
      const to = resolveReceiverNode(p, nodesSnapshot);
      const anim = makeAnimatedPacket(p, from, to);
      setAnimations((prev) => {
        const { next, overflowed } = capAnimations(pruneExpired(prev, performance.now()), anim);
        if (overflowed) setOverflowCount((c) => c + 1);
        return next;
      });
    },
    [queryClient],
  );

  const onWsEvent = useCallback(
    (evt: WsEvent) => {
      if (evt.kind === "node") {
        upsertNode(evt.data);
      } else if (evt.kind === "packet") {
        addPacket(evt.data, nodesRef.current);
      } else if (evt.kind === "message") {
        upsertMessage(evt.data);
      }
    },
    [upsertNode, addPacket, upsertMessage],
  );

  const wsState = useWebSocket("/ws/live", onWsEvent, authVersion, handlePossibleAuthFailure);

  // Entering replay fetches real historical packets from the store via the
  // existing, already-real `/api/packets?before=` cursor (this endpoint has
  // persisted history since phase 1 — it is not a stub). Exiting just drops
  // back to the live in-memory buffer. Nothing here touches the WebSocket
  // subscription or the map's live animations — see TimelinePanel.tsx.
  const enterReplay = useCallback((atMs: number) => {
    setReplay({ active: true, atMs, packets: [], loading: true, error: null });
    const before = new Date(atMs).toISOString();
    apiGet<Packet[]>(`/api/packets?before=${encodeURIComponent(before)}&limit=100`)
      .then((pkts) => setReplay({ active: true, atMs, packets: pkts, loading: false, error: null }))
      .catch((e) =>
        setReplay({ active: true, atMs, packets: [], loading: false, error: e instanceof Error ? e.message : "Failed to load history" }),
      );
  }, []);

  const exitReplay = useCallback(() => setReplay(REPLAY_IDLE), []);

  const replaySinglePacket = useCallback(
    (packet: Packet) => {
      const fromId = packet.from_id != null ? String(packet.from_id) : null;
      const from = fromId ? nodesRef.current.get(fromId) ?? null : null;
      const to = resolveReceiverNode(packet, nodesRef.current);
      const anim = makeAnimatedPacket(packet, from, to);
      setAnimations((prev) => {
        const { next } = capAnimations(pruneExpired(prev, performance.now()), anim);
        return next;
      });
    },
    // nodesRef (declared above) is a stable ref object read at call time,
    // not a value dependency.
    [],
  );

  // The real /api/observers (confirmed live 2026-09-20) is the richer,
  // dedicated registry — CoreScope, meshview, and our own first-party
  // transports all show up here with their trust tier. /api/stats'
  // `observers` list is kept as a fallback merge source for an older
  // backend build or a transient gap, not the primary source anymore.
  const observersPoll = useGlobalQuery<ObserverInfo[]>(["global", "observers"], "/api/observers", 20000);

  const observers = observersPoll.data ?? [];

  // Stale-data header note (data-layer slice 2, 2026-09-30 — owner's
  // decision). Reuses GET /api/audit/recompute_status purely for its
  // recompute_interval_minutes number — nothing about the Analysis/Audit
  // tabs' own recompute result is read or touched here (that page's data
  // path is being rewritten on another branch). Polled slowly (this is
  // config, not live data) and persisted like the rest of the global set
  // so the threshold itself survives a reload/offline gap the same way the
  // data it's measuring does.
  const recomputeStatusPoll = useGlobalQuery<AuditRecomputeStatus>(
    ["global", "auditRecomputeStatus"],
    "/api/audit/recompute_status",
    60000,
  );
  // Default 15 (matching the backend's own documented default) ONLY until
  // the real setting has ever loaded — never hard-coded once it has.
  const recomputeIntervalMinutes = recomputeStatusPoll.data?.recompute_interval_minutes ?? 15;

  // "The newest successful refresh of the global data" — the MOST RECENT
  // of every persisted global query's own dataUpdatedAt (0 for one that's
  // never resolved, which Math.max naturally ignores as long as at least
  // one has). As long as ANY one of these is still refreshing
  // successfully, the app is genuinely live and no note shows; only once
  // ALL of them have gone this long without a successful response
  // (matches exactly the /api-unreachable scenario, or a persisted cache
  // opened long after the last real session) does the newest of them cross
  // the threshold. scopedStats is deliberately excluded — its query key
  // changes with the active filter, so "newest" for it doesn't mean the
  // same thing as for the others.
  const newestGlobalDataAtMs = Math.max(
    featuresPoll.dataUpdatedAt,
    healthPoll.dataUpdatedAt,
    statsPoll.dataUpdatedAt,
    nodesQuery.dataUpdatedAt,
    packetsQuery.dataUpdatedAt,
    observersPoll.dataUpdatedAt,
    regionsPoll.dataUpdatedAt,
  );
  const staleNote = staleDataNote(newestGlobalDataAtMs, recomputeIntervalMinutes);

  const observerTrust = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of statsPoll.data?.observers ?? []) m.set(o.id, String((o as { trust_tier?: unknown }).trust_tier ?? ""));
    for (const o of observers) if (o.id && o.trust_tier) m.set(o.id, o.trust_tier);
    return m;
  }, [statsPoll.data, observers]);

  // Sweep expired animations off a ticking interval (they self-limit via
  // animationLifetimeMs — pulse-only ones expire fast, ones with a real
  // ray live a bit longer; this just clears the array so it doesn't grow
  // unbounded).
  useEffect(() => {
    const id = window.setInterval(() => {
      setAnimations((prev) => {
        const next = pruneExpired(prev, performance.now());
        return next.length === prev.length ? prev : next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, []);

  const backendReachable = healthPoll.everSucceeded && wsState !== "closed";

  const loadNodeDetail = useCallback(async (id: string) => {
    const cached = nodeDetailCache.current.get(id);
    if (cached) return cached;
    try {
      const detail = await apiGet<NodeDetail>(`/api/nodes/${encodeURIComponent(id)}`);
      nodeDetailCache.current.set(id, detail);
      forceDetailBump((n) => n + 1);
      return detail;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo<AppDataValue>(
    () => ({
      features: featuresPoll.data ?? [],
      featuresError: featuresPoll.error,
      featuresEverLoaded: featuresPoll.everSucceeded,
      health: healthPoll.data,
      healthError: healthPoll.error,
      healthEverLoaded: healthPoll.everSucceeded,
      stats: statsPoll.data,
      statsError: statsPoll.error,
      statsEverLoaded: statsPoll.everSucceeded,
      staleNote,
      scopedStats: scopedStatsPoll.data,
      scopedStatsError: scopedStatsPoll.error,
      nodes,
      nodesEverLoaded,
      packets,
      packetsEverLoaded,
      animations,
      overflowCount,
      wsState,
      backendReachable,
      selection,
      select: setSelection,
      nodeDetailCache: nodeDetailCache.current,
      loadNodeDetail,
      themePref,
      setThemePref,
      effectiveTheme,
      availableProtocols: availableProtocolsList,
      protocolFilter,
      setProtocolFilter,
      tagFilter,
      setTagFilter,
      provenanceScope,
      setProvenanceScope,
      meshviewRouteWalks,
      reportMeshviewFrameMs,
      corescorePathWalks,
      observers,
      observerTrust,
      replay,
      enterReplay,
      exitReplay,
      replaySinglePacket,
      regions,
      regionsAvailable,
      selectedRegions,
      setSelectedRegions,
      hideOutsideRegions,
      setHideOutsideRegions,
      regionsForNode,
      nearRegionBoundary,
      messages,
      upsertMessage,
      simulatedNodes,
      simulatedNodesAvailable,
      refreshSimulatedNodes,
      displaySettings: displaySettingsPoll.data,
      displaySettingsAvailable: displaySettingsPoll.everSucceeded,
    }),
    [
      featuresPoll.data,
      featuresPoll.error,
      featuresPoll.everSucceeded,
      healthPoll.data,
      healthPoll.error,
      healthPoll.everSucceeded,
      statsPoll.data,
      statsPoll.error,
      statsPoll.everSucceeded,
      staleNote,
      scopedStatsPoll.data,
      displaySettingsPoll.data,
      displaySettingsPoll.everSucceeded,
      scopedStatsPoll.error,
      nodes,
      nodesEverLoaded,
      packets,
      packetsEverLoaded,
      animations,
      overflowCount,
      availableProtocolsList,
      protocolFilter,
      setProtocolFilter,
      tagFilter,
      setTagFilter,
      provenanceScope,
      setProvenanceScope,
      meshviewRouteWalks,
      reportMeshviewFrameMs,
      corescorePathWalks,
      observers,
      observerTrust,
      replay,
      enterReplay,
      exitReplay,
      replaySinglePacket,
      regions,
      regionsAvailable,
      selectedRegions,
      hideOutsideRegions,
      regionsForNode,
      nearRegionBoundary,
      wsState,
      backendReachable,
      selection,
      loadNodeDetail,
      themePref,
      setThemePref,
      effectiveTheme,
      messages,
      upsertMessage,
      simulatedNodes,
      simulatedNodesAvailable,
      refreshSimulatedNodes,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppData(): AppDataValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAppData must be used within AppDataProvider");
  return ctx;
}
